/**
 * SheetError primitive (issue #78).
 *
 * The sanctioned inline **submit-failure** surface for a BottomSheet, and
 * the convention it carries: **a sheet that stays open across a mutation
 * owns an inline error surface.** (A sheet that closes on dispatch may
 * toast — CategoryOverrideSheet does exactly that, and is right to.)
 * A toast is not an option from inside a sheet that stays open. The
 * modal/sheet tier is z-50 and ToastViewport is z-40 (Pattern 7 in
 * docs/ARCHITECTURE.md), so a toast raised by a mutation the sheet
 * started is painted *behind* the scrim that hides it — the adult taps
 * "Slett", nothing visibly happens, and the failure is silent.
 *
 * Five sheets had independently converged on the same markup before this
 * component existed — `<p class="text-xs text-bad text-center"
 * role="alert">` — and two more had not, which is what made the
 * convention worth extracting rather than documenting. Those exact
 * classes are therefore the contract, not an implementation detail:
 * adopting the primitive at an existing call site must leave the
 * rendered DOM byte-identical.
 *
 * `role="alert"` is deliberate inside an `aria-modal` dialog, and the
 * reasoning was spelled out at RewardApprovalsRoute.tsx's reject error (the
 * file is gone since #155; RewardClaimCard rejects inline now): a
 * message inserted silently into a focus-trapped dialog is the
 * screen-reader form of exactly the bug this surface exists to fix.
 * T-04-toastTone's "never role=alert" is a rule about the toast host and
 * does not reach inline errors.
 *
 * No message renders **nothing at all** — no wrapper, no reserved layout
 * space. That is load-bearing rather than tidy: apps/web/e2e/shopping-visual.spec.ts
 * screenshots the sweep sheet in its idle state against a committed
 * baseline, and an always-present empty slot would drift it.
 *
 * `data-testid` is required because every call site owns a distinct one
 * that tests and e2e specs already query by name; there is no sensible
 * shared default.
 *
 * Submit-level only, and not the only inline error in the app. Field-level
 * validation errors stay where they are — EventEditSheet's two carry an
 * `id` for `aria-describedby`, which this component has no slot for. Two
 * deliberate visual deviations also survive: ChoreCompleteSheet's photo
 * error (`text-warn`, a warning rather than a failure) and the
 * boxed-berry form in `features/rewards/` (issue #90). Sibling primitive:
 * ErrorState, for a whole surface that failed to load rather than one
 * submit that failed.
 *
 * Why a separate component and not an `error` prop on BottomSheet: the
 * sheet's API is `{open, onClose, children, title, snapPoints}` and its
 * header calls it "intentionally headless beyond a small chrome header".
 * With one opaque `children` blob and no footer slot, a prop could only
 * render at a fixed edge of the body — which is not where any of the seven
 * call sites put it.
 *
 * Author note: React 19 — no forwardRef needed (refs are regular props).
 */
import type { JSX } from 'react';

export interface SheetErrorProps {
  /**
   * Failure copy. `null` / `undefined` / '' render nothing. Required
   * rather than optional: under `exactOptionalPropertyTypes` a call site
   * holding `string | undefined` (EventEditSheet's `errors.submit`)
   * cannot pass an optional prop at all, and "no error" is the component's
   * main state, not an omission.
   */
  message: string | null | undefined;
  /** The call site's own testid — distinct per sheet, so it has no default. */
  'data-testid': string;
}

export function SheetError({
  message,
  'data-testid': testId,
}: SheetErrorProps): JSX.Element | null {
  if (!message) return null;

  return (
    <p className="text-xs text-bad text-center" role="alert" data-testid={testId}>
      {message}
    </p>
  );
}
